import type { Denial } from './api'

/**
 * En cuál de los cuatro estados está un listado (§13, *Definition of Done*).
 *
 * La especificación pide que cada pantalla distinga cargando, vacío, error y degradado. Existían
 * los cuatro por separado, pero **nadie los había puesto juntos**, y de ahí salía el defecto que
 * esta función arregla: catorce listados decidían qué mostrar solo con `length === 0`, así que
 * mientras los datos venían en camino —y también cuando la llamada había fallado— la pantalla
 * afirmaba «no hay ninguno».
 *
 * Es una diferencia de fondo y no de cortesía: «no hay unidades conformadas» es un dato
 * operacional con el que alguien decide algo. Decirlo cuando la verdad es «no lo sabemos» es
 * inventarle al turno un hecho que nadie comprobó.
 */
export type DataState =
  /** Todavía no ha llegado nada y no se sabe si hay o no. */
  | 'loading'
  /** La llamada falló y no hay nada que mostrar. */
  | 'error'
  /** Llegó la respuesta y de verdad no hay nada. */
  | 'empty'
  /** Hay datos, pero la última recarga falló: lo que se ve es de antes. */
  | 'stale'
  /** Hay datos y están al día. */
  | 'ready'

/**
 * @param loaded Si alguna carga de esta área terminó bien alguna vez.
 * @param problem El último rechazo o fallo, o `null` si la última carga salió bien.
 * @param count Cuántos elementos hay para mostrar.
 */
export function dataState(loaded: boolean, problem: Denial | null, count: number): DataState {
  // Sin nada cargado, el fallo es lo único que se sabe; sin fallo, sigue viniendo en camino.
  if (!loaded) return problem ? 'error' : 'loading'
  // Cargó alguna vez y ahora falla. Con datos viejos en pantalla es «degradado» y **se siguen
  // mostrando**: en un centro de mando, datos de hace un minuto valen más que una pantalla en
  // blanco, siempre que diga que son de hace un minuto. Sin datos viejos es un error a secas.
  if (problem) return count > 0 ? 'stale' : 'error'
  return count === 0 ? 'empty' : 'ready'
}

/** Si hay que pintar la lista, aparte de cualquier aviso. */
export function showsRows(state: DataState): boolean {
  return state === 'ready' || state === 'stale'
}

/**
 * Cada carga que ocurre por su cuenta, con su propia marca de «ya trajo algo alguna vez».
 *
 * No coincide con las áreas de navegación, y por eso no reutiliza ese tipo: el Centro de Mando
 * hace tres cargas independientes —el turno, la bitácora y la entrega— y cada una puede haber
 * llegado o no sin que las otras lo sepan. Una sola bandera por área haría que la bitácora dijera
 * «no hay movimientos» porque las unidades sí habían cargado.
 */
export type LoadKey =
  | 'command' | 'timeline' | 'handover' | 'analyst' | 'planner'
  | 'strategy' | 'insight' | 'admin' | 'fleet' | 'hr'
